import React from 'react';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import LimiteErreur from '../../shared/components/LimiteErreur';
import Graph3Vagues from '../../shared/charts/Graph3Vagues';
import CarteRisquePRA from '../../shared/charts/CarteRisquePRA';
import { PALETTE_RISQUE_ACTUEL } from '../../shared/constants';

const TYPES_RISQUE = [
  { value: 'courant', label: 'Courant', champVague: 'dataVagues7', champCarte: 'risques7' },
  { value: 'exceptionnel', label: 'Exceptionnel', champVague: 'dataVagues20', champCarte: 'risques20' },
  { value: 'catastrophique', label: 'Catastrophique', champVague: 'dataVagues50', champCarte: 'risques50' },
];

const ECHELLES = [
  { value: 'pra', label: 'Petite région agricole' },
  { value: 'ra', label: 'Région agricole' },
  { value: 'departement', label: 'Département' },
  { value: 'region', label: 'Région' },
];

/** Valeur d'une des 3 vagues au rang de l'exploitation (dataPerso[0].x). */
function valeurAuRang(serie, rang) {
  return serie?.find((p) => p.x === rang)?.y;
}

/**
 * Comparer / Risque actuel.
 *
 * Contrairement aux autres pages 2035-2050/Comparer, pas d'horizon ni de
 * scénario climatique dans la sidebar : "risque actuel" compare l'exploitation
 * à aujourd'hui, seuls le niveau de risque (carte) et l'échelle géographique
 * changent.
 *
 * Deux appels séparés : le graphique 3 vagues (route réelle confirmée) et la
 * carte (route encore non confirmée) ne viennent pas de la même réponse.
 */
export default function ComparerRisqueActuel() {
  const { idExploit } = useOAD();
  const [type, setType] = React.useState(TYPES_RISQUE[0].value);
  const [echelle, setEchelle] = React.useState(ECHELLES[0].value);

  const parametresVagues = React.useMemo(
    () => ({ idExploitation: idExploit }),
    [idExploit]
  );
  const parametresCarte = React.useMemo(
    () => ({ idExploitation: idExploit, echelle }),
    [idExploit, echelle]
  );

  const { data, loading, error } = useApiData(api.getRisqueActuel, parametresVagues);
  const { data: donneesCarte } = useApiData(api.getCarteRisqueActuel, parametresCarte);

  // Rang de l'exploitation dans la population : constant, indépendant du
  // niveau de risque (dataPerso est la même ligne verticale pour les 3
  // vagues, seule sa hauteur d'affichage varie selon le niveau choisi).
  const rang = data?.dataPerso?.[0]?.x;
  const nbExploitations = data?.dataVagues7?.length;

  // Les 3 encarts ne sont pas renvoyés à part par l'API : on les lit
  // directement dans dataVagues7/20/50 au rang de l'exploitation.
  const Risques7 = valeurAuRang(data?.dataVagues7, rang);
  const Risques20 = valeurAuRang(data?.dataVagues20, rang);
  const Risques50 = valeurAuRang(data?.dataVagues50, rang);

  // Repères de quartiles (25%/50%/25%) : pas renvoyés par l'API, calculés
  // depuis la taille réelle de la population reçue.
  const dataQ1 = nbExploitations ? [{ x: nbExploitations / 4, y: 0 }, { x: nbExploitations / 4, y: 80 }] : [];
  const dataQ2 = nbExploitations ? [{ x: nbExploitations / 2, y: 0 }, { x: nbExploitations / 2, y: 80 }] : [];
  const dataQ3 = nbExploitations ? [{ x: (nbExploitations * 3) / 4, y: 0 }, { x: (nbExploitations * 3) / 4, y: 80 }] : [];
  // Fixe à 80, même si ça rogne une partie des données (demandé tel quel) :
  // plus de calcul dynamique sur le max de dataVagues50.
  const maximum = 80;

  return (
    <div className="oad-contenu">
      <SidebarParametres
        niveau1="comparer-comprendre"
        niveau2="comparer"
        niveau3="risque-actuel"
        idExploit={idExploit}
      >
        <label className="sidebar-champ">
          <span className="sidebar-label">Niveau de risque :</span>
          <select value={type} onChange={(e) => setType(e.target.value)}>
            {TYPES_RISQUE.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>

        <label className="sidebar-champ">
          <span className="sidebar-label">Géographie</span>
          <select value={echelle} onChange={(e) => setEchelle(e.target.value)}>
            {ECHELLES.map((e) => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
        </label>
      </SidebarParametres>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="ra-titre">Quels est mon niveau de risques ?</h2>
          <p className="ig-tableau-sous-titre">
            Votre exploitation comparée aux {nbExploitations ?? '…'} exploitations spécialisées en
            grandes cultures des différentes régions agricoles, en % de perte par rapport au
            produit brut.
          </p>

          {error && <p className="tdb-erreur">{error}</p>}
          {loading && <p className="tdb-chargement">Chargement…</p>}

          {data && (
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
              <div className="dispLeftOpt" style={{ flex: '0 0 55%', minWidth: 0 }}>
                <div style={{ marginTop: '10px' }}>
                  <LimiteErreur cle="graph3vagues">
                    <Graph3Vagues
                      dataPerso={data.dataPerso}
                      dataVagues7={data.dataVagues7}
                      dataVagues20={data.dataVagues20}
                      dataVagues50={data.dataVagues50}
                      maximum={maximum}
                      dataQ1={dataQ1}
                      dataQ2={dataQ2}
                      dataQ3={dataQ3}
                    />
                  </LimiteErreur>
                </div>
                <div className="row axeX" style={{ position: 'relative', height: '36px' }}>
                  <div className="paragraphe" style={{ position: 'absolute', left: '16%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>25% les moins exposées</div>
                  <div className="paragraphe" style={{ position: 'absolute', left: '54%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>50%</div>
                  <div className="paragraphe" style={{ position: 'absolute', left: '86%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>25% les plus exposées</div>
                </div>
              </div>

              <div className="column marge" style={{ flex: '0 0 40%', alignItems: 'stretch' }}>
                <div className="videBloc row videBloc7">
                  <div className="pleinBloc row pleinBloc7">
                    <div className="titreBloc">Courant </div>
                  </div>
                  <div className="paragrapheBloc paragrapheBloc7">{Math.round(Risques7 * 10) / 10} % <span style={{ fontWeight: 'normal', fontSize: '70%' }}> du produit brut </span> </div>
                </div>

                <div className="videBloc row videBloc20">
                  <div className="pleinBloc row pleinBloc20">
                    <div className="titreBloc">Exceptionnel </div>
                  </div>
                  <div className="paragrapheBloc paragrapheBloc20">{Math.round(Risques20 * 10) / 10} % <span style={{ fontWeight: 'normal', fontSize: '70%' }}> du produit brut </span> </div>
                </div>

                <div className="videBloc row videBloc50">
                  <div className="pleinBloc row pleinBloc50">
                    <div className="titreBloc">Catastrophique </div>
                  </div>
                  <div className="paragrapheBloc paragrapheBloc50"> {Math.round(Risques50 * 10) / 10} % <span style={{ fontWeight: 'normal', fontSize: '70%' }}> du produit brut </span> </div>
                </div>

                <p className="ig-tableau-sous-titre">
                  <span style={{ fontWeight: '1000px', fontSize: '25px', color: 'red' }}>---</span> Le risque de votre exploitation rapport aux exploitations moyennes
                </p>
                <div className="ig-tableau-sous-titre"> Le risque <span style={{ color: '#FFC000' }}>"courant"</span> se produit de manière fréquente (tous les 7 ans environ) et à des effets peu importants sur la perte de rendement. </div>
                <div className="ig-tableau-sous-titre"> Le risque <span style={{ color: '#0027CC' }}>"exceptionnel"</span> se produit de manière un peu plus fréquente (tous les 20 ans environ) et à des effets plus importants sur la perte de rendement.  </div>
                <div className="ig-tableau-sous-titre"> Le risque <span style={{ color: '#DB04B9' }}>"catastrophique"</span> se produit de manière un peu fréquente (tous les 50 ans environ) et à des effets importants sur la perte de rendement.  </div>
                <div className="ig-tableau-sous-titre" style={{ fontWeight: 'bold' }}> La base de comparaison se base soit sur des exploitations types à 100% grandes cultures soit sur des exploitations comprenant de la viticulture (avec ou sans grandes cultures)</div>
              </div>
            </div>
          )}
        </section>

        {donneesCarte?.carte && (
          <section className="oad-card">
            <h2 className="tdb-section-titre">Les risques moyens de ma région</h2>
            <p className="ig-tableau-sous-titre">
              Le point noir correspond à la localisation du siège social de votre exploitation. La
              couleur de la carte correspond au risque de perte en % de produit brut des
              exploitations types de chaque entité (petites régions agricoles, régions agricoles,
              départements et régions).
            </p>
            <LimiteErreur cle={`carte-${echelle}-${type}`}>
              <CarteRisquePRA
                data={donneesCarte.carte.data}
                champ={TYPES_RISQUE.find((t) => t.value === type)?.champCarte ?? 'risques7'}
                palette={PALETTE_RISQUE_ACTUEL}
                pointExploitation={donneesCarte.pointExploitation}
              />
            </LimiteErreur>
          </section>
        )}
      </main>
    </div>
  );
}
